'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import Autoplay from 'embla-carousel-autoplay';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { QuotationIcon, StarIcon } from '@/components/icon/star';
import { CardDataProps } from '../data/testimonials';

// 默认导入水印去除评论数据作为后备数据
import defaultTestimonials from '../data/testimonials';

/**
 * @warn Swiper Loop Warning: The number of slides is not enough for loop mode, it will be disabled and not function properly.
 * @issue https://github.com/nolimits4web/swiper/issues/7586
 */

interface PerfectionSwiperProps {
  translations?: {
    title: string;
  };
}

const PerfectionSwiper = ({ translations }: PerfectionSwiperProps) => {
  const title = translations?.title || 'Proof of perfection';
  const pathname = usePathname();
  const [testimonials, setTestimonials] =
    useState<CardDataProps[]>(defaultTestimonials);

  // 提取当前页面类型，如watermark-removal、text-removal等
  const getPageType = useCallback(() => {
    const path = pathname.split('/');
    // 寻找路径中的最后一个非空段
    for (let i = path.length - 1; i >= 0; i--) {
      if (path[i] && path[i] !== '(image-rmw)') {
        return path[i];
      }
    }
    return 'watermark-removal'; // 默认值
  }, [pathname]);

  useEffect(() => {
    const loadTestimonials = async () => {
      const pageType = getPageType();

      try {
        // 动态导入对应页面的评论数据
        const testimonialsModule = await import(
          `../../${pageType}/data/testimonials`
        );
        setTestimonials(testimonialsModule.default);
      } catch (error) {
        // 如果导入失败，使用默认的水印去除评论数据
        console.warn(
          `Failed to load testimonials for ${pageType}, using default testimonials instead.`
        );
        setTestimonials(defaultTestimonials);
      }
    };

    loadTestimonials();
  }, [getPageType]);

  // 自动播放插件
  const plugin = useMemo(
    () =>
      Autoplay({
        delay: 4000,
      }),
    []
  );

  return (
    <div className='mt-20 h-full w-full px-5 lg:px-28'>
      <h2 className='mb-[1.88rem] w-full text-center text-2xl font-semibold text-gray-800 md:mb-14 md:text-[44px]'>
        {title}
      </h2>

      {/* 移动端轮播 - 单个卡片显示 */}
      <div className='block lg:hidden'>
        <Carousel
          opts={{
            align: 'center',
            loop: true,
          }}
          plugins={[plugin]}
          className='w-full'
        >
          <CarouselContent className='-ml-5'>
            {testimonials.map((item) => (
              <CarouselItem key={item.id} className='basis-full pl-5'>
                <div className='mx-4 h-[400px] rounded-xl border border-neutral-300/50 bg-white p-8 shadow-lg'>
                  <div>
                    <QuotationIcon />
                  </div>
                  <div className='mt-6 h-[240px] font-medium lg:h-[230px] lg:text-sm lg:font-medium xl:text-base 2xl:text-lg'>
                    {item.content}
                  </div>
                  <div className='mt-4 flex'>
                    <span className='text-lg font-medium lg:text-base xl:text-xl 2xl:text-2xl'>
                      {item.name}
                    </span>
                    <span className='i ml-5 flex h-6 items-center lg:h-5 xl:h-6 2xl:h-8'>
                      <StarIcon />
                      <StarIcon />
                      <StarIcon />
                      <StarIcon />
                      <StarIcon />
                    </span>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className='absolute -left-2 top-1/2 z-10 hidden -translate-y-1/2 md:block'>
            <CarouselPrevious className='h-10 w-10' />
          </div>
          <div className='absolute -right-2 top-1/2 z-10 hidden -translate-y-1/2 md:block'>
            <CarouselNext className='h-10 w-10' />
          </div>
        </Carousel>
      </div>

      {/* 桌面端轮播 - 每个卡片占据 1/3 宽度 */}
      <div className='hidden lg:block'>
        <Carousel
          opts={{
            align: 'center',
            loop: true,
          }}
          plugins={[plugin]}
          className='w-full'
        >
          <CarouselContent className='-ml-5'>
            {testimonials.map((item) => (
              <CarouselItem key={item.id} className='basis-1/3 pl-5'>
                <div className='mx-4 h-[380px] rounded-xl border border-neutral-300/50 bg-white p-8 shadow-lg'>
                  <div>
                    <QuotationIcon />
                  </div>
                  <div className='mt-6 h-[230px] font-medium lg:text-sm lg:font-medium xl:text-base 2xl:text-lg'>
                    {item.content}
                  </div>
                  <div className='mt-4 flex'>
                    <span className='text-lg font-medium lg:text-base xl:text-xl 2xl:text-2xl'>
                      {item.name}
                    </span>
                    <span className='i ml-5 flex h-6 items-center lg:h-5 xl:h-6 2xl:h-8'>
                      <StarIcon />
                      <StarIcon />
                      <StarIcon />
                      <StarIcon />
                      <StarIcon />
                    </span>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className='absolute -left-6 top-1/2 z-10 hidden -translate-y-1/2 md:block'>
            <CarouselPrevious className='h-10 w-10' />
          </div>
          <div className='absolute -right-6 top-1/2 z-10 hidden -translate-y-1/2 md:block'>
            <CarouselNext className='h-10 w-10' />
          </div>
        </Carousel>
      </div>
    </div>
  );
};

export default PerfectionSwiper;
